import { EffectCallback, useEffect, useRef } from 'react';

/**
 * Use setTimeout with Hooks in a declarative way.
 *
 * @see https://stackoverflow.com/a/59274757/3723993
 * @see https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */
export default function useTimeout(
    callback: EffectCallback,
    delay: number | undefined,
    deps: any[] = []
) {
    const timeoutRef = useRef<number | null>(null);
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        if (typeof delay === 'number') {
            timeoutRef.current = window.setTimeout(
                () => callbackRef.current(),
                delay
            );

            // Clear timeout if the components is unmounted or the delay changes:
            return () => window.clearTimeout(timeoutRef.current || 0);
        }

        return () => {
            /* noop */
        };
    }, [delay, ...deps]);

    const cancel = () =>
        timeoutRef.current && window.clearTimeout(timeoutRef.current);
    const reset = () => {
        cancel();
        timeoutRef.current = window.setTimeout(callbackRef.current, delay);
    };

    return { reset, cancel };
}
